import { useState, useRef, useEffect, useImperativeHandle, forwardRef } from 'react';
import { createPortal } from 'react-dom';
import { FileText } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
} from '@mui/material';
import { PDFPinMarker } from './PDFPinMarker';
import { CompactViewerHeader } from './CompactViewerHeader';
import { ViewerInfoDrawer } from './ViewerInfoDrawer.jsx';
import { RecordingTimerChip } from './RecordingTimerChip';
import { FiberManualRecord as RecordIcon } from '@mui/icons-material';
import { keyframes } from '@mui/system';
import { useDrawingZoom } from '../../store/slices/useZoomStore.ts';
import { ErrorBoundary } from './ErrorBoundary';
import { IframePDFViewer } from './IframePDFViewer';
import logger from '../../utils/logger';
import { get } from '../../services/api.js';
const pulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.4;
  }
`;
export const PDFViewer = forwardRef(({
  fileName,
  fileUrl,
  fileData = null,
  pins = [],
  onDeletePin,
  onUpdatePinLabel,
  onUpdatePinPosition,
  onPinClick,
  onEditLocationID,
  onRecordingClick,
  onRecordingPlay,
  onContextMenu,
  recordings,
  currentSession,
  onFullscreenChange,
  isRecording,
  recordingDuration,
  onRecordingComplete
}, ref) => {
  // Generate stable drawing ID from fileName (used for persistence)
  const drawingId = `pdf-${fileName}`;

  // Get persistent zoom state from Redux store
  const {
    zoom: savedZoom,
    page: savedPage,
    rotation: savedRotation,
    setZoom: saveZoom,
    setPage: savePage,
    setRotation: saveRotation
  } = useDrawingZoom(drawingId);
  const [zoom, setZoom] = useState(savedZoom);
  const [currentPage, setCurrentPage] = useState(savedPage || 1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [rotation, setRotation] = useState(savedRotation);
  const [draggingMarkerId, setDraggingMarkerId] = useState(null);
  const [dragStart, setDragStart] = useState({
    x: 0,
    y: 0
  });
  const [editingPinId, setEditingPinId] = useState(null);
  const [editingPinOriginalPosition, setEditingPinOriginalPosition] = useState(null);
  const [infoDrawerOpen, setInfoDrawerOpen] = useState(false);
  const [activities, setActivities] = useState([]);
  const [isLoadingActivities, setIsLoadingActivities] = useState(false);

  // PDF.js state
  const [numPages, setNumPages] = useState(null);
  const [pageWidth, setPageWidth] = useState(0);
  const [pageHeight, setPageHeight] = useState(0);
  const [pdfError, setPdfError] = useState(null);
  const [documentLoaded, setDocumentLoaded] = useState(false);
  const [pdfDocument, setPdfDocument] = useState(null);

  // Pan mode state
  const [isPanMode, setIsPanMode] = useState(false);
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({
    x: 0,
    y: 0
  });
  const [panOffset, setPanOffset] = useState({
    x: 0,
    y: 0
  });
  const [spacebarPressed, setSpacebarPressed] = useState(false);

  // Touch gesture state
  const [touchStartDistance, setTouchStartDistance] = useState(null);
  const [touchStartZoom, setTouchStartZoom] = useState(100);
  const [lastTapTime, setLastTapTime] = useState(0);
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const fullscreenCanvasRef = useRef(null);
  const pdfDocumentRef = useRef(null);
  const totalPages = numPages || 1; // Default to 1 instead of 10
  const [workerReady, setWorkerReady] = useState(false);

  // Initialize PDF.js worker and load PDF.js library
  useEffect(() => {
    const loadPDFJS = async () => {
      try {
        // Load PDF.js from CDN if not already loaded
        if (typeof window !== 'undefined' && !window.pdfjsLib) {
          const script = document.createElement('script');
          script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
          script.async = true;
          document.head.appendChild(script);
          
          await new Promise((resolve, reject) => {
            script.onload = () => {
              // Set worker source
              if (window.pdfjsLib) {
                window.pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
                resolve();
              } else {
                reject(new Error('PDF.js failed to load'));
              }
            };
            script.onerror = reject;
          });
        }
        setWorkerReady(true);
        console.log('PDF.js worker ready');
      } catch (err) {
        console.warn('Failed to load PDF.js, falling back to iframe mode:', err);
        setWorkerReady(true); // Still set ready so iframe can work
      }
    };
    
    loadPDFJS();
  }, []);

  // Load PDF and get page count
  useEffect(() => {
    if (!fileUrl || !workerReady) return;

    let isCancelled = false;

    const loadPDFPageCount = async () => {
      try {
        // Check if this is a real PDF
        const isRealPDF = fileUrl && (fileUrl.toLowerCase().endsWith('.pdf') || fileUrl.startsWith('blob:') || fileUrl.startsWith('data:application/pdf'));
        
        if (!isRealPDF) {
          // For non-PDF files, set to 1
          if (!isCancelled) setNumPages(1);
          return;
        }

        // Try to get page count using PDF.js
        if (typeof window !== 'undefined' && window.pdfjsLib) {
          const loadingTask = window.pdfjsLib.getDocument(fileUrl);
          const pdf = await loadingTask.promise;
          
          if (isCancelled) {
            pdf.destroy();
            return;
          }
          
          const pageCount = pdf.numPages;
          setNumPages(pageCount);
          console.log(`PDF loaded: ${pageCount} pages`);
          
          // Store document reference for cleanup
          pdfDocumentRef.current = pdf;
        } else {
          // Fallback: try to parse PDF blob manually
          try {
            const response = await fetch(fileUrl);
            if (isCancelled) return;
            
            const blob = await response.blob();
            const arrayBuffer = await blob.arrayBuffer();
            const uint8Array = new Uint8Array(arrayBuffer);
            const text = new TextDecoder('latin1').decode(uint8Array);
            
            if (isCancelled) return;
            
            // Try to find page count in PDF structure
            // Look for /Count in the document catalog
            const countMatch = text.match(/\/Count\s+(\d+)/);
            if (countMatch) {
              const count = parseInt(countMatch[1], 10);
              if (count > 0) {
                setNumPages(count);
                console.log(`PDF page count detected: ${count} pages`);
                return;
              }
            }
            
            // Fallback: count /Type/Page occurrences (less reliable)
            const pageMatches = text.match(/\/Type\s*\/Page[^s]/g);
            if (pageMatches && pageMatches.length > 0) {
              setNumPages(pageMatches.length);
              console.log(`PDF page count estimated: ${pageMatches.length} pages`);
              return;
            }
          } catch (parseError) {
            console.warn('Failed to parse PDF for page count:', parseError);
          }
          
          // Last resort: set to null (will show as 1)
          if (!isCancelled) setNumPages(null);
        }
      } catch (error) {
        if (!isCancelled) {
          console.warn('Failed to get PDF page count:', error);
          // Don't set numPages, let it remain null (will default to 1)
        }
      }
    };

    loadPDFPageCount();

    // Cleanup function
    return () => {
      isCancelled = true;
    };
  }, [fileUrl, workerReady]);

  // Reset error state when file URL changes and cleanup previous document
  useEffect(() => {
    // Cleanup previous PDF document before loading new one
    if (pdfDocumentRef.current) {
      try {
        pdfDocumentRef.current.destroy();
        console.log('PDF document destroyed on file change');
      } catch (err) {
        console.warn('Error destroying previous PDF document:', err);
      }
      pdfDocumentRef.current = null;
    }
    setPdfError(null);
    setDocumentLoaded(false);
    setNumPages(null);
    setPdfDocument(null);

    // Cleanup on unmount
    return () => {
      if (pdfDocumentRef.current) {
        try {
          pdfDocumentRef.current.destroy();
          console.log('PDF document destroyed on unmount');
        } catch (err) {
          console.warn('Error destroying PDF document on unmount:', err);
        }
        pdfDocumentRef.current = null;
      }
    };
  }, [fileUrl]);

  // Sync local state with Redux store on mount and when drawingId changes
  // This ensures we get the latest persisted values from localStorage when switching PDFs
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log(`[PDFViewer] Syncing state from Redux for ${drawingId}:`, {
        savedZoom,
        savedPage,
        savedRotation,
        currentZoom: zoom,
        currentPage,
        currentRotation: rotation,
      });
    }
    
    // Sync zoom from Redux store if available and different
    if (savedZoom !== undefined && savedZoom !== zoom) {
      console.log(`[PDFViewer] Syncing zoom from ${zoom} to ${savedZoom}`);
      setZoom(savedZoom);
    }
    // Sync page from Redux store if available and different
    if (savedPage !== undefined && savedPage !== currentPage) {
      console.log(`[PDFViewer] Syncing page from ${currentPage} to ${savedPage}`);
      setCurrentPage(savedPage);
    }
    // Sync rotation from Redux store if available and different
    if (savedRotation !== undefined && savedRotation !== rotation) {
      console.log(`[PDFViewer] Syncing rotation from ${rotation} to ${savedRotation}`);
      setRotation(savedRotation);
    }
  }, [drawingId]); // Only run when drawingId changes (switching PDFs or initial mount)

  // Persist zoom/page/rotation changes to Redux store
  // Only save if the value has actually changed to avoid unnecessary updates
  useEffect(() => {
    saveZoom(zoom);
  }, [zoom, saveZoom]);
  useEffect(() => {
    savePage(currentPage);
  }, [currentPage, savePage]);
  useEffect(() => {
    saveRotation(rotation);
  }, [rotation, saveRotation]);

  // Debug: Log fileData when it changes
  useEffect(() => {
    console.log("[PDFViewer] fileData changed:", fileData);
    console.log("[PDFViewer] fileData?.id:", fileData?.id);
    console.log("[PDFViewer] fileData keys:", fileData ? Object.keys(fileData) : 'null');
  }, [fileData]);

  // Fetch activity data for a file
  const fetchActivities = async (fileId) => {
    console.log("[fetchActivities] Called with fileId:", fileId);
    
    if (!fileId) {
      console.warn("[fetchActivities] No fileId provided, returning empty array");
      setActivities([]);
      return;
    }

    console.log("[fetchActivities] Starting API call to /directory/" + fileId + "/activity");
    setIsLoadingActivities(true);
    
    try {
      const endpoint = `/directory/${fileId}/activity`;
      console.log("[fetchActivities] Making GET request to:", endpoint);
      
      const response = await get(endpoint);
      console.log("[fetchActivities] API response received:", response);
      console.log("[fetchActivities] Response type:", typeof response);
      console.log("[fetchActivities] Response keys:", response ? Object.keys(response) : 'null');
      
      // Handle response structure: response.data.activities
      const activitiesData = response?.data?.activities || response?.activities || response?.data || [];
      console.log("[fetchActivities] Extracted activities data:", activitiesData);
      console.log("[fetchActivities] Activities is array:", Array.isArray(activitiesData));
      console.log("[fetchActivities] Activities length:", activitiesData?.length);
      
      const finalActivities = Array.isArray(activitiesData) ? activitiesData : [];
      console.log("[fetchActivities] Setting activities state with:", finalActivities);
      setActivities(finalActivities);
    } catch (error) {
      console.error("[fetchActivities] Error fetching activities:", error);
      console.error("[fetchActivities] Error details:", {
        message: error?.message,
        status: error?.status,
        data: error?.data,
        stack: error?.stack
      });
      setActivities([]);
    } finally {
      console.log("[fetchActivities] Setting isLoadingActivities to false");
      setIsLoadingActivities(false);
    }
  };

  // Handle info click - fetch activities and open drawer
  const handleInfoClick = () => {
    console.log("========================================");
    console.log("[handleInfoClick] Info icon clicked");
    console.log("[handleInfoClick] fileData:", fileData);
    console.log("[handleInfoClick] fileUrl:", fileUrl);
    console.log("[handleInfoClick] fileName:", fileName);
    
    // Try multiple possible ID fields from fileData
    let fileId = fileData?.id || fileData?._id || fileData?.file_id || fileData?.directory_id;
    
    // If no ID from fileData, try to extract from fileUrl
    if (!fileId && fileUrl) {
      console.log("[handleInfoClick] Trying to extract ID from fileUrl");
      // Try to extract ID from URL patterns like:
      // /api/files/{id}_{filename}.pdf
      // /files/{id}_{filename}
      // The ID is typically the numeric part before the underscore
      const urlMatch = fileUrl.match(/\/files\/(\d+)(?:_|$)/);
      if (urlMatch && urlMatch[1]) {
        fileId = urlMatch[1];
        console.log("[handleInfoClick] Extracted ID from URL:", fileId);
      } else {
        // Try alternative pattern: just get the filename part and use it as ID
        const filenameMatch = fileUrl.match(/\/files\/([^\/]+)/);
        if (filenameMatch && filenameMatch[1]) {
          // If filename contains underscore, use the part before it as ID
          const filename = filenameMatch[1];
          const idFromFilename = filename.split('_')[0];
          if (idFromFilename && /^\d+$/.test(idFromFilename)) {
            fileId = idFromFilename;
            console.log("[handleInfoClick] Extracted ID from filename:", fileId);
          }
        }
      }
    }
    
    console.log("[handleInfoClick] Checking ID fields:");
    console.log("  - fileData?.id:", fileData?.id);
    console.log("  - fileData?._id:", fileData?._id);
    console.log("  - fileData?.file_id:", fileData?.file_id);
    console.log("  - fileData?.directory_id:", fileData?.directory_id);
    console.log("[handleInfoClick] Final fileId:", fileId);
    
    if (fileId) {
      console.log("[handleInfoClick] Calling fetchActivities with fileId:", fileId);
      fetchActivities(fileId);
    } else {
      console.warn("[handleInfoClick] No file ID found, cannot fetch activities");
      console.warn("[handleInfoClick] fileData:", fileData);
      console.warn("[handleInfoClick] fileUrl:", fileUrl);
      setActivities([]);
    }
    
    console.log("[handleInfoClick] Opening info drawer");
    setInfoDrawerOpen(true);
    console.log("========================================");
  };

  // Clear activities when drawer closes
  useEffect(() => {
    if (!infoDrawerOpen) {
      setActivities([]);
    }
  }, [infoDrawerOpen]);

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 25, 400));
  };
  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 25, 25));
  };
  const handleZoomChange = e => {
    const value = parseInt(e.target.value);
    if (!isNaN(value) && value >= 25 && value <= 400) {
      setZoom(value);
    }
  };
  const handlePreviousPage = () => {
    setCurrentPage(prev => Math.max(prev - 1, 1));
  };
  const handleNextPage = () => {
    setCurrentPage(prev => Math.min(prev + 1, totalPages));
  };
  const handlePageChange = e => {
    const value = parseInt(e.target.value);
    if (!isNaN(value) && value >= 1 && value <= totalPages) {
      setCurrentPage(value);
    }
  };
  const handlePrint = () => {
    if (fileUrl) {
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = fileName;
      link.click();
    }
  };
  const toggleFullscreen = () => {
    const newFullscreenState = !isFullscreen;
    setIsFullscreen(newFullscreenState);
    if (onFullscreenChange) {
      onFullscreenChange(newFullscreenState);
    }
  };
  const handleRotate = () => {
    setRotation(prev => (prev + 90) % 360);
  };

  // New zoom/pan handlers for context menu
  const handleContextZoomIn = () => {
    setZoom(prev => Math.min(prev + 25, 400));
  };
  const handleContextZoomOut = () => {
    setZoom(prev => Math.max(prev - 25, 25));
  };
  const handleZoomToFit = () => {
    // Calculate zoom level to fit content to viewport
    if (containerRef.current && canvasRef.current) {
      const container = containerRef.current;
      const canvas = canvasRef.current;
      const containerWidth = container.clientWidth - 32; // Account for padding
      const containerHeight = container.clientHeight - 32;
      const canvasWidth = 800; // Base canvas width
      const canvasHeight = 1130; // Base canvas height

      const widthRatio = containerWidth / canvasWidth;
      const heightRatio = containerHeight / canvasHeight;

      // Use the smaller ratio to fit completely within viewport
      const fitZoom = Math.floor(Math.min(widthRatio, heightRatio) * 100);
      setZoom(Math.max(25, Math.min(400, fitZoom)));
      setPanOffset({
        x: 0,
        y: 0
      });

      // Reset scroll position
      container.scrollLeft = 0;
      container.scrollTop = 0;
    }
  };
  const handleActualSize = () => {
    setZoom(100);
    setPanOffset({
      x: 0,
      y: 0
    });

    // Reset scroll position
    if (containerRef.current) {
      containerRef.current.scrollLeft = 0;
      containerRef.current.scrollTop = 0;
    }
  };
  const handleTogglePanMode = () => {
    setIsPanMode(!isPanMode);
  };
  const handleResetView = () => {
    setZoom(100);
    setPanOffset({
      x: 0,
      y: 0
    });
    setRotation(0);
    setIsPanMode(false);
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = e => {
      // Spacebar for temporary pan mode
      if (e.code === 'Space' && !spacebarPressed && !editingPinId) {
        e.preventDefault();
        setSpacebarPressed(true);
      }

      // F key: Toggle fullscreen (YouTube standard)
      if (e.key === 'f' || e.key === 'F') {
        e.preventDefault();
        toggleFullscreen();
      }

      // Ctrl/Cmd + 0: Zoom to fit
      if ((e.ctrlKey || e.metaKey) && e.key === '0') {
        e.preventDefault();
        handleZoomToFit();
      }

      // Ctrl/Cmd + 1: Actual size
      if ((e.ctrlKey || e.metaKey) && e.key === '1') {
        e.preventDefault();
        handleActualSize();
      }

      // Ctrl/Cmd + Plus: Zoom in
      if ((e.ctrlKey || e.metaKey) && (e.key === '+' || e.key === '=')) {
        e.preventDefault();
        handleContextZoomIn();
      }

      // Ctrl/Cmd + Minus: Zoom out
      if ((e.ctrlKey || e.metaKey) && (e.key === '-' || e.key === '_')) {
        e.preventDefault();
        handleContextZoomOut();
      }
    };
    const handleKeyUp = e => {
      // Release spacebar pan mode
      if (e.code === 'Space') {
        setSpacebarPressed(false);
        setIsPanning(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [spacebarPressed, editingPinId, isFullscreen]);

  // Mouse wheel zoom (Ctrl + Wheel) - for both regular and fullscreen mode
  useEffect(() => {
    const container = containerRef.current;
    const fullscreenContainer = fullscreenCanvasRef.current;
    const handleWheel = e => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();

        // Zoom centered on cursor position
        const delta = e.deltaY > 0 ? -25 : 25;
        setZoom(prev => Math.max(25, Math.min(400, prev + delta)));
      }
    };
    if (container) {
      container.addEventListener('wheel', handleWheel, {
        passive: false
      });
    }
    if (fullscreenContainer) {
      fullscreenContainer.addEventListener('wheel', handleWheel, {
        passive: false
      });
    }
    return () => {
      if (container) {
        container.removeEventListener('wheel', handleWheel);
      }
      if (fullscreenContainer) {
        fullscreenContainer.removeEventListener('wheel', handleWheel);
      }
    };
  }, [isFullscreen]);

  // Pan with middle mouse button or spacebar + left click
  const handleContainerMouseDown = e => {
    // Middle mouse button (button 1) or left click with spacebar
    if (e.button === 1 || e.button === 0 && (spacebarPressed || isPanMode)) {
      e.preventDefault();
      setIsPanning(true);
      setPanStart({
        x: e.clientX,
        y: e.clientY
      });
    }
  };
  const handleContainerMouseMove = e => {
    if (isPanning) {
      const container = isFullscreen ? fullscreenCanvasRef.current : containerRef.current;
      if (container) {
        const deltaX = e.clientX - panStart.x;
        const deltaY = e.clientY - panStart.y;
        container.scrollLeft -= deltaX;
        container.scrollTop -= deltaY;
        setPanStart({
          x: e.clientX,
          y: e.clientY
        });
      }
    }
  };
  const handleContainerMouseUp = () => {
    setIsPanning(false);
  };

  // Touch gestures for zoom and pan
  const handleTouchStart = e => {
    if (e.touches.length === 2) {
      // Two-finger pinch to zoom
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = Math.hypot(touch2.clientX - touch1.clientX, touch2.clientY - touch1.clientY);
      setTouchStartDistance(distance);
      setTouchStartZoom(zoom);
    } else if (e.touches.length === 1) {
      // Single finger drag to pan (when zoomed)
      if (zoom > 100) {
        const touch = e.touches[0];
        setPanStart({
          x: touch.clientX,
          y: touch.clientY
        });
        setIsPanning(true);
      }

      // Double-tap detection
      const currentTime = Date.now();
      const tapGap = currentTime - lastTapTime;
      if (tapGap < 300 && tapGap > 0) {
        // Double-tap detected - zoom to 200% at tap point
        if (zoom === 100) {
          setZoom(200);
        } else {
          setZoom(100);
        }
      }
      setLastTapTime(currentTime);
    }
  };
  const handleTouchMove = e => {
    if (e.touches.length === 2 && touchStartDistance) {
      // Pinch zoom
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = Math.hypot(touch2.clientX - touch1.clientX, touch2.clientY - touch1.clientY);
      const scale = distance / touchStartDistance;
      const newZoom = Math.max(25, Math.min(400, touchStartZoom * scale));
      setZoom(newZoom);
    } else if (e.touches.length === 1 && isPanning && containerRef.current) {
      // Single finger pan
      const touch = e.touches[0];
      const deltaX = touch.clientX - panStart.x;
      const deltaY = touch.clientY - panStart.y;
      const container = containerRef.current;
      container.scrollLeft -= deltaX;
      container.scrollTop -= deltaY;
      setPanStart({
        x: touch.clientX,
        y: touch.clientY
      });
    }
  };
  const handleTouchEnd = () => {
    setTouchStartDistance(null);
    setIsPanning(false);
  };

  // Update cursor style based on pan mode
  const getViewerCursorStyle = () => {
    if (isPanning) return 'grabbing';
    if (spacebarPressed || isPanMode) return 'grab';
    if (editingPinId) return 'crosshair';
    if (draggingMarkerId) return 'move';
    return 'default';
  };

  // Handle mouse down for location adding
  const handleMouseDown = (e, isFullscreenMode = false) => {
    const canvas = isFullscreenMode ? fullscreenCanvasRef.current : canvasRef.current;

    // Check if clicking on empty space to deselect - removed location adding logic
    const target = e.target;
    if (target === canvas || target.classList.contains('pdf-canvas')) {
      // Future: handle location select logic here if needed
    }
  };

  // Handle marker mouse down - removed as not used
  const handleMarkerMouseDown = (e, markerId) => {
    e.stopPropagation();
    // Legacy code removed
  };

  // Handle marker dragging - removed as not used
  const handleMarkerMove = (e, isFullscreenMode = false) => {
    // Legacy code removed
  };

  // Handle marker mouse up
  const handleMarkerMouseUp = () => {
    setDraggingMarkerId(null);
  };
  useEffect(() => {
    if (draggingMarkerId) {
      const handleGlobalMouseUp = () => {
        setDraggingMarkerId(null);
      };
      window.addEventListener('mouseup', handleGlobalMouseUp);
      return () => window.removeEventListener('mouseup', handleGlobalMouseUp);
    }
  }, [draggingMarkerId]);

  // Handle pin editing with ESC key
  useEffect(() => {
    const handleKeyDown = e => {
      if (e.key === 'Escape' && editingPinId) {
        // Cancel editing and restore original position
        if (editingPinOriginalPosition) {
          onUpdatePinPosition?.(editingPinId, editingPinOriginalPosition);
        }
        setEditingPinId(null);
        setEditingPinOriginalPosition(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [editingPinId, editingPinOriginalPosition, onUpdatePinPosition]);

  // Handle pin editing mouse movement
  const handlePinEditMouseMove = (e, isFullscreenMode = false) => {
    if (editingPinId) {
      const canvas = isFullscreenMode ? fullscreenCanvasRef.current : canvasRef.current;
      if (canvas) {
        const rect = canvas.getBoundingClientRect();
        const x = (e.clientX - rect.left) / rect.width * 100;
        const y = (e.clientY - rect.top) / rect.height * 100;
        const clampedX = Math.max(0, Math.min(100, x));
        const clampedY = Math.max(0, Math.min(100, y));
        onUpdatePinPosition?.(editingPinId, {
          x: clampedX,
          y: clampedY
        });
      }
    }
  };

  // Handle pin editing click to commit
  const handlePinEditClick = () => {
    if (editingPinId) {
      setEditingPinId(null);
      setEditingPinOriginalPosition(null);
    }
  };

  // Start editing a pin
  const handleStartEditPin = pinId => {
    const pin = pins.find(p => p.id === pinId);
    if (pin) {
      setEditingPinId(pinId);
      setEditingPinOriginalPosition({
        ...pin.position
      });
    }
  };

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    handleZoomIn: handleContextZoomIn,
    handleZoomOut: handleContextZoomOut,
    handleZoomToFit,
    handleActualSize,
    handleTogglePanMode,
    handleResetView,
    getZoom: () => zoom,
    getIsPanMode: () => isPanMode,
    handleEditPinPosition: handleStartEditPin
  }));

  // Render PDF content
  const renderPDFContent = (isFullscreenMode = false) => {
    const canvasRefToUse = isFullscreenMode ? fullscreenCanvasRef : canvasRef;

    // Check if this is a real PDF or an image
    const isRealPDF = fileUrl && (fileUrl.toLowerCase().endsWith('.pdf') || fileUrl.startsWith('blob:') || fileUrl.startsWith('data:application/pdf'));

    // Calculate dimensions based on mode
    const baseWidth = isFullscreenMode ? typeof window !== 'undefined' ? window.innerWidth - 100 : 1200 : 800;
    const baseHeight = isFullscreenMode ? typeof window !== 'undefined' ? window.innerHeight - 100 : 1600 : 1130;
    const handlePDFContextMenu = e => {
      if (onContextMenu) {
        e.preventDefault();
        const canvas = canvasRefToUse.current;
        if (canvas) {
          const rect = canvas.getBoundingClientRect();
          const x = (e.clientX - rect.left) / rect.width * 100;
          const y = (e.clientY - rect.top) / rect.height * 100;
          onContextMenu(e, {
            x,
            y
          });
        }
      }
    };
    const handleCanvasMouseMove = e => {
      if (editingPinId) {
        const canvas = canvasRefToUse.current;
        if (canvas) {
          const rect = canvas.getBoundingClientRect();
          const x = (e.clientX - rect.left) / rect.width * 100;
          const y = (e.clientY - rect.top) / rect.height * 100;
          const clampedX = Math.max(0, Math.min(100, x));
          const clampedY = Math.max(0, Math.min(100, y));
          onUpdatePinPosition?.(editingPinId, {
            x: clampedX,
            y: clampedY
          });
        }
      }
    };
    const handleCanvasClick = e => {
      if (editingPinId) {
        // Commit the pin position
        setEditingPinId(null);
        setEditingPinOriginalPosition(null);
        e.stopPropagation();
      }
    };

    // FOR REAL PDFs: Simple, clean rendering with zoom using PDF.js
    if (isRealPDF) {
      const scale = zoom / 100; // Convert zoom percentage to scale factor

      return <div ref={canvasRefToUse} className="relative bg-white pdf-canvas" style={{
        display: 'inline-block',
        transform: isFullscreenMode ? `rotate(${rotation}deg)` : 'none',
        transformOrigin: 'center',
        transition: 'transform 0.3s ease',
        cursor: getViewerCursorStyle(),
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }} onContextMenu={handlePDFContextMenu} onMouseMove={handleCanvasMouseMove} onClick={handleCanvasClick}>
        {/* PDF.js Document Component - Only render when worker is ready */}
        {!workerReady ? <div style={{
          width: `${baseWidth}px`,
          height: `${baseHeight}px`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#f2f2f2'
        }}>
          <Typography sx={{
            fontFamily: 'Roboto, sans-serif',
            fontSize: '14px',
            color: '#4a5565'
          }}>
            Initializing PDF viewer...
          </Typography>
        </div> : <ErrorBoundary fallback={<div style={{
          width: `${baseWidth}px`,
          height: `${baseHeight}px`,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#f2f2f2',
          gap: '16px',
          padding: '24px'
        }}>
          <Typography sx={{
            fontFamily: 'Roboto, sans-serif',
            fontSize: '16px',
            fontWeight: 600,
            color: '#d32f2f'
          }}>
            PDF Rendering Error
          </Typography>
          <Typography sx={{
            fontFamily: 'Roboto, sans-serif',
            fontSize: '12px',
            color: '#4a5565',
            textAlign: 'center',
            maxWidth: '400px'
          }}>
            Unable to render PDF. The document may be corrupted or in an unsupported format.
          </Typography>
          <Typography sx={{
            fontFamily: 'Roboto, sans-serif',
            fontSize: '11px',
            color: '#9e9e9e',
            textAlign: 'center'
          }}>
            Try refreshing the page or using a different PDF file.
          </Typography>
        </div>}>
          {/* Iframe-based PDF Viewer for maximum compatibility */}
          <div style={{
            position: 'relative',
            width: `${baseWidth * scale}px`,
            height: `${baseHeight * scale}px`
          }}>
            <IframePDFViewer 
              key={fileUrl} 
              file={fileUrl} 
              page={currentPage} 
              style={{
                width: `${baseWidth * scale}px`,
                height: `${baseHeight * scale}px`
              }}
              pointerEvents="auto"
              onLoadSuccess={pdf => {
              // Page count should already be set by the useEffect above
              // Only update if we got a valid numPages from the callback
              if (pdf?.numPages) {
                setNumPages(pdf.numPages);
              }
              setTimeout(() => {
                setDocumentLoaded(true);
              }, 50);
              setPdfError(null);
              logger.log('PDF loaded successfully in iframe:', fileName);
            }} onLoadError={error => {
              console.error('PDF load error:', error);
              console.error('File URL:', fileUrl);
              setPdfError(error);
              setDocumentLoaded(false);
              setPdfDocument(null);
              setNumPages(null);
            }} loading={<div style={{
              width: `${baseWidth}px`,
              height: `${baseHeight}px`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: '#f2f2f2'
            }}>
              <Typography sx={{
                fontFamily: 'Roboto, sans-serif',
                fontSize: '14px',
                color: '#4a5565'
              }}>
                Loading PDF... {fileName}
              </Typography>
            </div>} error={<div style={{
              width: `${baseWidth}px`,
              height: `${baseHeight}px`,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              background: '#f2f2f2',
              gap: '16px',
              padding: '24px'
            }}>
              <Typography sx={{
                fontFamily: 'Roboto, sans-serif',
                fontSize: '16px',
                fontWeight: 600,
                color: '#d32f2f'
              }}>
                Failed to load PDF
              </Typography>
              <Typography sx={{
                fontFamily: 'Roboto, sans-serif',
                fontSize: '12px',
                color: '#4a5565',
                textAlign: 'center'
              }}>
                This may be a CORS issue or the PDF file may be corrupted.
              </Typography>
              <Typography sx={{
                fontFamily: 'Roboto, sans-serif',
                fontSize: '11px',
                color: '#9e9e9e',
                textAlign: 'center',
                maxWidth: '600px',
                wordBreak: 'break-all'
              }}>
                URL: {fileUrl}
              </Typography>
            </div>} />

            {/* Transparent Overlay - Captures pointer events for pan mode, pin editing, and context menu */}
            <div 
              className="absolute inset-0 w-full h-full" 
              style={{
                // Block pointer events when editing pins, in pan mode, or spacebar is pressed
                // Otherwise allow events to pass through to iframe for normal scrolling
                pointerEvents: (editingPinId || isPanMode || spacebarPressed) ? 'all' : 'none',
                cursor: getViewerCursorStyle(),
                zIndex: (editingPinId || isPanMode || spacebarPressed) ? 10 : 0,
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0
              }} 
              onContextMenu={(e) => {
                // Always handle context menu
                if (onContextMenu) {
                  e.preventDefault();
                  const canvas = canvasRefToUse.current;
                  if (canvas) {
                    const rect = canvas.getBoundingClientRect();
                    const x = (e.clientX - rect.left) / rect.width * 100;
                    const y = (e.clientY - rect.top) / rect.height * 100;
                    onContextMenu(e, {
                      x,
                      y
                    });
                  }
                }
              }} 
              onMouseMove={(e) => {
                // Handle mouse move for pin editing and pan mode
                if (editingPinId) {
                  handleCanvasMouseMove(e);
                }
                if (isPanMode || spacebarPressed || isPanning) {
                  handleContainerMouseMove(e);
                }
              }} 
              onMouseDown={(e) => {
                // Handle mouse down for pan mode and pin editing
                if (isPanMode || spacebarPressed) {
                  handleContainerMouseDown(e);
                }
                if (editingPinId) {
                  handleCanvasClick(e);
                }
              }}
              onMouseUp={(e) => {
                // Handle mouse up for pan mode
                if (isPanning) {
                  handleContainerMouseUp();
                }
              }}
              onClick={(e) => {
                // Only handle click when editing pins
                if (editingPinId) {
                  handleCanvasClick(e);
                }
              }}
            />

            {/* Pins */}
            {pins?.map(pin => <PDFPinMarker key={pin.id} pin={pin} onDelete={onDeletePin || (() => { })} onUpdateLabel={onUpdatePinLabel || (() => { })} onUpdatePosition={onUpdatePinPosition || (() => { })} onPinClick={onPinClick} onEditLocationID={onEditLocationID} onRecordingClick={onRecordingClick} onRecordingPlay={onRecordingPlay} onEditPosition={handleStartEditPin} isEditingPosition={pin.id === editingPinId} recordings={recordings} currentSession={currentSession} />)}
          </div>
        </ErrorBoundary>}
      </div>;
    }

    // FOR IMAGE FILES: Keep simulated drawing with decorative elements
    return <div ref={canvasRefToUse} className="relative bg-white pdf-canvas shadow-lg" style={{
      width: `${baseWidth * (zoom / 100)}px`,
      height: `${baseHeight * (zoom / 100)}px`,
      transform: isFullscreenMode ? `rotate(${rotation}deg)` : 'none',
      transformOrigin: 'center',
      transition: 'transform 0.3s ease',
      cursor: getViewerCursorStyle()
    }} onContextMenu={handlePDFContextMenu} onMouseMove={handleCanvasMouseMove} onClick={handleCanvasClick}>
      {/* PDF Content - Simulated drawing representation */}
      <div className="absolute inset-0 flex flex-col p-8" style={{
        gap: '16px'
      }}>
        {/* Drawing Title Block */}
        <div className="border-2 border-[#212121] p-4">
          <div className="flex items-center justify-between" style={{
            gap: '16px'
          }}>
            <div>
              <h4 className="text-[#212121]" style={{
                fontFamily: 'Roboto, sans-serif',
                fontSize: '16px',
                fontWeight: '700',
                lineHeight: '24px'
              }}>
                {fileName.replace(/\.(pdf|dwg)$/i, '')}
              </h4>
              <p className="text-[#4a5565]" style={{
                fontFamily: 'Roboto, sans-serif',
                fontSize: '12px',
                lineHeight: '16px'
              }}>
                Sheet {currentPage} of {totalPages}
              </p>
            </div>
            <div className="text-right">
              <p className="text-[#4a5565]" style={{
                fontFamily: 'Roboto, sans-serif',
                fontSize: '10px',
                lineHeight: '14px'
              }}>
                SCALE: 1:100
              </p>
              <p className="text-[#4a5565]" style={{
                fontFamily: 'Roboto, sans-serif',
                fontSize: '10px',
                lineHeight: '14px'
              }}>
                DATE: {new Date().toLocaleDateString()}
              </p>
            </div>
          </div>
        </div>

        {/* Drawing Content Area */}
        <div className="flex-1 border border-[#e5e7eb] relative overflow-hidden">
          {/* Grid Pattern */}
          <svg className="absolute inset-0 w-full h-full" style={{
            opacity: 0.2
          }}>
            <defs>
              <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#4a5565" strokeWidth="0.5" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>

          {/* Render image using SVG for sample drawings */}
          <svg className="absolute inset-0 w-full h-full">
            <image href={fileUrl} x="0" y="0" width="100%" height="100%" preserveAspectRatio="xMidYMid meet" style={{
              opacity: 0.95
            }} />
          </svg>

          {/* Dimension lines */}
          <div className="absolute top-2 left-1/2 transform -translate-x-1/2" style={{
            zIndex: 10
          }}>
            <p className="text-[#ff8555]" style={{
              fontFamily: 'Roboto, sans-serif',
              fontSize: '10px',
              fontWeight: '600'
            }}>
              Page {currentPage}
            </p>
          </div>
        </div>

        {/* Notes Section */}
        <div className="border-t border-[#e5e7eb] pt-2">
          <p className="text-[#4a5565]" style={{
            fontFamily: 'Roboto, sans-serif',
            fontSize: '9px',
            lineHeight: '12px'
          }}>
            <strong>NOTES:</strong> This is a construction drawing representation. All dimensions in millimeters unless noted otherwise.
          </p>
        </div>
      </div>

      {/* Pins */}
      {pins?.map(pin => <PDFPinMarker key={pin.id} pin={pin} onDelete={onDeletePin || (() => { })} onUpdateLabel={onUpdatePinLabel || (() => { })} onUpdatePosition={onUpdatePinPosition || (() => { })} onPinClick={onPinClick} onEditLocationID={onEditLocationID} onRecordingClick={onRecordingClick} onRecordingPlay={onRecordingPlay} onEditPosition={handleStartEditPin} isEditingPosition={pin.id === editingPinId} recordings={recordings} currentSession={currentSession} />)}
    </div>;
  };
  return <>
    <div className="h-full flex flex-col bg-white border border-[#e5e7eb] rounded-lg overflow-hidden shadow-sm" style={{display:'flex',flexDirection:'column',height:'100%',width:'100%'}}>
      {/* Compact PDF Viewer Header */}
      <CompactViewerHeader fileName={fileName} showPageNav={true} currentPage={currentPage} totalPages={totalPages} onPreviousPage={handlePreviousPage} onNextPage={handleNextPage} zoom={zoom} onZoomIn={handleZoomIn} onZoomOut={handleZoomOut} onZoomReset={() => setZoom(100)} onInfoClick={handleInfoClick} onPrint={handlePrint} onRotate={handleRotate} onFullscreen={toggleFullscreen} onPanMode={handleTogglePanMode} onResetView={handleResetView} onActualSize={handleActualSize} onZoomToFit={handleZoomToFit} isPanMode={isPanMode} isFullscreen={isFullscreen} minZoom={25} maxZoom={400} />

      {/* PDF Viewer Content */}
      <div ref={containerRef} className="flex-1 relative bg-[#4a5565]/5 overflow-auto" style={{
        flex: 1,
        position: 'relative',
        backgroundColor: '#4a5565/5',
        overflow: 'auto',
        cursor: getViewerCursorStyle()
      }} onMouseDown={e => {
        handleContainerMouseDown(e);
        handleMouseDown(e, false);
      }} onMouseMove={e => {
        handleContainerMouseMove(e);
        handleMarkerMove(e, false);
      }} onMouseUp={() => {
        handleContainerMouseUp();
        handleMarkerMouseUp();
      }} onMouseLeave={() => {
        handleContainerMouseUp();
        handleMarkerMouseUp();
      }} onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd}>
        {fileUrl ? zoom > 100 ?
          // When zoomed in, render content directly without wrapper to allow proper overflow scrolling  
          renderPDFContent(false) :
          // When zoomed out or at 100%, center the content
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100%',
            minWidth: '100%',
            padding: '4px'
          }}>
            <div style={{
              display: 'inline-block',
              minWidth: '100%',
              minHeight: '100%',
              textAlign: 'center',
              padding: '4px'
            }}>
              <div style={{
                display: 'inline-block',
                verticalAlign: 'middle'
              }}>
                {renderPDFContent(false)}
              </div>
              {/* Vertical centering helper */}
              <div style={{
                display: 'inline-block',
                height: '100%',
                verticalAlign: 'middle',
                width: 0
              }} />
            </div>
          </div> : <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100%',
            minWidth: '100%',
            padding: '16px'
          }}>
          <div className="text-center">
            <FileText size={64} className="text-[#e5e7eb] mx-auto" style={{
              marginBottom: '16px'
            }} />
            <p className="text-[#4a5565]" style={{
              fontFamily: 'Roboto, sans-serif',
              fontSize: '14px',
              lineHeight: '20px'
            }}>
              No PDF file selected
            </p>
            <p className="text-[#4a5565]" style={{
              fontFamily: 'Roboto, sans-serif',
              fontSize: '12px',
              lineHeight: '16px',
              marginTop: '8px'
            }}>
              Select a drawing to view
            </p>
          </div>
        </div>}
      </div>
    </div>

    {/* Fullscreen Dialog */}
    <Dialog
      open={isFullscreen}
      onClose={() => setIsFullscreen(false)}
      PaperProps={{
        sx: {
          m: 0,
          p: 0,
          width: "100vw",
          height: "100vh",
          maxWidth: "100vw",
          maxHeight: "100vh",
          borderRadius: 0,
          bgcolor: "#212121",
        },
      }}
      BackdropProps={{
        invisible: true, // prevent dark overlay
      }}
      hideBackdrop
    >
      <DialogContent
        sx={{
          p: 0,
          m: 0,
          width: "100vw",
          height: "100vh",
          bgcolor: "#212121",
          position: "relative",
          overflow: "hidden",
        }}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* GRID LAYOUT */}
        <Box
          sx={{
            display: "grid",
            gridTemplateRows: "auto 1fr",
            position: "absolute",
            inset: 0,
            width: "100vw",
            height: "100vh",
            bgcolor: "#212121",
          }}
        >
          {/* HEADER */}
          <CompactViewerHeader
            fileName={fileName}
            onClose={() => setIsFullscreen(false)}
            showPageNav={true}
            currentPage={currentPage}
            totalPages={totalPages}
            onPreviousPage={handlePreviousPage}
            onNextPage={handleNextPage}
            zoom={zoom}
            onZoomIn={handleZoomIn}
            onZoomOut={handleZoomOut}
            onZoomReset={() => setZoom(100)}
            onInfoClick={handleInfoClick}
            onPrint={handlePrint}
            onRotate={handleRotate}
            onFullscreen={() => setIsFullscreen(false)}
            onPanMode={handleTogglePanMode}
            onResetView={handleResetView}
            onActualSize={handleActualSize}
            onZoomToFit={handleZoomToFit}
            isPanMode={isPanMode}
            isFullscreen={isFullscreen}
            minZoom={25}
            maxZoom={400}
            darkMode={true}
          />

          {/* CONTENT */}
          <Box
            ref={fullscreenCanvasRef}
            sx={{
              position: "relative",
              width: "100%",
              height: "100%",
              backgroundColor: "#4a5565",
              cursor: getViewerCursorStyle(),
              overflow: "auto",
              minHeight: 0,
            }}
            onMouseDown={(e) => {
              handleContainerMouseDown(e);
              handleMouseDown(e, true);
            }}
            onMouseMove={(e) => {
              handleContainerMouseMove(e);
              handleMarkerMove(e, true);
            }}
            onMouseUp={() => {
              handleContainerMouseUp();
              handleMarkerMouseUp();
            }}
            onMouseLeave={() => {
              handleContainerMouseUp();
              handleMarkerMouseUp();
            }}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {/* PDF RENDER */}
            {fileUrl ? (
              zoom > 100 ? (
                renderPDFContent(true)
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: "100%",
                    minWidth: "100%",
                    p: "8px",
                  }}
                >
                  <Box
                    sx={{
                      display: "inline-block",
                      minWidth: "100%",
                      minHeight: "100%",
                      textAlign: "center",
                      p: "4px",
                    }}
                  >
                    <Box sx={{ display: "inline-block", verticalAlign: "middle" }}>
                      {renderPDFContent(true)}
                    </Box>
                    <Box
                      sx={{
                        display: "inline-block",
                        height: "100%",
                        verticalAlign: "middle",
                        width: 0,
                      }}
                    />
                  </Box>
                </Box>
              )
            ) : (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: "100%",
                  minWidth: "100%",
                  p: 2,
                  textAlign: "center",
                }}
              >
                <Typography sx={{ color: "#fff", fontSize: "14px" }}>
                  No PDF file selected
                </Typography>
              </Box>
            )}

            {/* RECORDING OVERLAY */}
            {isFullscreen && isRecording && (
              <>
                {/* Freeze Overlay */}
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    bgcolor: "rgba(0, 0, 0, 0.3)",
                    pointerEvents: "none",
                    zIndex: 10000,
                  }}
                />

                {/* REC Label */}
                <Box
                  sx={{
                    position: "absolute",
                    top: 80,
                    right: 16,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    bgcolor: "rgba(0, 0, 0, 0.8)",
                    px: 2,
                    py: 1,
                    borderRadius: "8px",
                    zIndex: 10001,
                  }}
                >
                  <RecordIcon
                    sx={{
                      fontSize: 16,
                      color: "#d32f2f",
                      animation: `${pulse} 1.5s infinite`,
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: "13px",
                      fontWeight: 600,
                      color: "white",
                    }}
                  >
                    REC
                  </Typography>
                </Box>

                {/* Timer */}
                {recordingDuration !== undefined && onRecordingComplete && (
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: 16,
                      left: 16,
                      zIndex: 10001,
                    }}
                  >
                    <RecordingTimerChip
                      duration={recordingDuration}
                      onComplete={onRecordingComplete}
                    />
                  </Box>
                )}
              </>
            )}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
    
    {/* Info Drawer - Rendered via Portal for fullscreen mode to appear above Dialog */}
    {isFullscreen && typeof document !== 'undefined' && createPortal(
      <ViewerInfoDrawer 
        open={infoDrawerOpen} 
        onClose={() => setInfoDrawerOpen(false)} 
        fileName={fileName}
        container={document.body}
        zIndex={1600}
        hideBackdrop={true}
        fileDetails={(() => {
            // Format file size
            const formatFileSize = (bytes) => {
              if (!bytes) return 'N/A';
              if (bytes < 1024) return `${bytes} B`;
              if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
              return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
            };

            // Format date
            const formatDate = (dateString) => {
              if (!dateString) return 'N/A';
              try {
                const date = new Date(dateString);
                return date.toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: 'short', 
                  day: 'numeric' 
                });
              } catch (e) {
                return 'N/A';
              }
            };

            // Get file type from mime type or file extension
            const getFileType = () => {
              if (fileData?.mime_type || fileData?.mimeType) {
                const mimeType = fileData.mime_type || fileData.mimeType;
                if (mimeType.includes('pdf')) return 'PDF Document';
                if (mimeType.includes('image')) return 'Image';
                return mimeType.split('/')[1]?.toUpperCase() || 'Document';
              }
              if (fileData?.file_type || fileData?.fileType) {
                const fileType = (fileData.file_type || fileData.fileType).toUpperCase();
                return fileType === 'PDF' ? 'PDF Document' : `${fileType} Document`;
              }
              return 'PDF Document';
            };

            // Get location/path
            const getLocation = () => {
              if (fileData?.directory_path) return fileData.directory_path;
              if (fileData?.path) return fileData.path;
              if (fileData?.parent_id) return 'My Documents';
              return 'My Documents';
            };

            return {
              owner: fileData?.owner || 'Unknown',
              dateModified: formatDate(fileData?.updated_at || fileData?.dateModified),
              dateCreated: formatDate(fileData?.created_at || fileData?.dateCreated),
              fileType: getFileType(),
              size: formatFileSize(fileData?.size),
              description: fileData?.description || '',
              location: getLocation()
            };
          })()}
          activities={activities}
          isLoadingActivities={isLoadingActivities}
          comments={[]}
        />,
      document.body
    )}

    {/* Regular Info Drawer - Only show when not in fullscreen */}
    {!isFullscreen && (
      <ViewerInfoDrawer 
        open={infoDrawerOpen} 
        onClose={() => setInfoDrawerOpen(false)} 
        fileName={fileName} 
        fileDetails={(() => {
          // Format file size
          const formatFileSize = (bytes) => {
            if (!bytes) return 'N/A';
            if (bytes < 1024) return `${bytes} B`;
            if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
            return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
          };

          // Format date
          const formatDate = (dateString) => {
            if (!dateString) return 'N/A';
            try {
              const date = new Date(dateString);
              return date.toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'short', 
                day: 'numeric' 
              });
            } catch (e) {
              return 'N/A';
            }
          };

          // Get file type from mime type or file extension
          const getFileType = () => {
            if (fileData?.mime_type || fileData?.mimeType) {
              const mimeType = fileData.mime_type || fileData.mimeType;
              if (mimeType.includes('pdf')) return 'PDF Document';
              if (mimeType.includes('image')) return 'Image';
              return mimeType.split('/')[1]?.toUpperCase() || 'Document';
            }
            if (fileData?.file_type || fileData?.fileType) {
              const fileType = (fileData.file_type || fileData.fileType).toUpperCase();
              return fileType === 'PDF' ? 'PDF Document' : `${fileType} Document`;
            }
            return 'PDF Document';
          };

          // Get location/path
          const getLocation = () => {
            if (fileData?.directory_path) return fileData.directory_path;
            if (fileData?.path) return fileData.path;
            if (fileData?.parent_id) return 'My Documents';
            return 'My Documents';
          };

          return {
            owner: fileData?.owner || 'Unknown',
            dateModified: formatDate(fileData?.updated_at || fileData?.dateModified),
            dateCreated: formatDate(fileData?.created_at || fileData?.dateCreated),
            fileType: getFileType(),
            size: formatFileSize(fileData?.size),
            description: fileData?.description || '',
            location: getLocation()
          };
        })()}
        activities={activities}
        isLoadingActivities={isLoadingActivities}
        comments={[]}
      />
    )}
  </>;
});   
PDFViewer.displayName = 'PDFViewer';